import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { apiEndpoints } from '../services/api';
import CalendarPicker from './CalendarPicker';
import CustomSelect from './CustomSelect';
import AutocompleteInput from './AutocompleteInput';
import { useFormData } from '../hooks/useFormData';

const DriverForm = ({ isOpen, onClose, driver, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    role: '',
    license: '',
    licenseExp: '',
    status: 'Activ',
  });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiEndpoints.companies.list().then(res => setCompanies(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (driver) {
      setFormData({
        name: driver.name || '',
        company: driver.company || '',
        role: driver.role || '',
        license: driver.license || '',
        licenseExp: driver.licenseExp ? driver.licenseExp.slice(0, 10) : '',
        status: driver.status || 'Activ',
      });
    } else {
      setFormData({ name: '', company: '', role: '', license: '', licenseExp: '', status: 'Activ' });
    }
  }, [driver, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      alert(error.response?.data?.message || 'Error saving driver. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fd = useFormData();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={driver ? 'Edit Driver' : 'Add Driver'} size="sm">
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input
              type="text"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g. Ion Popescu"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Company *</label>
            {companies.length > 0 ? (
              <CustomSelect name="company" className="form-input" value={formData.company} onChange={handleChange} required>
                <option value="">Select company…</option>
                {companies.map(c => (
                  <option key={c._id} value={c.label || c.key}>{c.label || c.key}</option>
                ))}
              </CustomSelect>
            ) : (
              <input
                type="text"
                name="company"
                className="form-input"
                value={formData.company}
                onChange={handleChange}
                required
                placeholder="e.g. Transport SRL"
              />
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Role</label>
            <AutocompleteInput name="role" value={formData.role} onChange={handleChange}
              suggestions={fd.roles} placeholder="e.g. Operations Lead" />
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <CustomSelect name="status" className="form-input" value={formData.status} onChange={handleChange}>
              <option value="Activ">Activ</option>
              <option value="Inactiv">Inactiv</option>
            </CustomSelect>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">License Category</label>
            <AutocompleteInput name="license" value={formData.license} onChange={handleChange}
              suggestions={fd.licenseCategories} placeholder="e.g. B, C, CE" />
          </div>
          <div className="form-group">
            <label className="form-label">License Expires</label>
            <CalendarPicker
              name="licenseExp"
              value={formData.licenseExp}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Saving...' : (driver ? 'Update Driver' : 'Add Driver')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default DriverForm;
